import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2, Wrench, History } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import BikeForm from "@/components/bikes/BikeForm";
import RepairHistoryModal from "@/components/bikes/RepairHistoryModal";
import { Bike } from "@/types/models";
import { getAllBikes, deleteBike } from "@/integrations/supabase/services";
import { useAuth } from "@/context/AuthContext"; 

const Bikes = () => {
  const [selectedBike, setSelectedBike] = useState<Bike | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [isRepairHistoryModalOpen, setIsRepairHistoryModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const { data: bikes, isLoading } = useQuery<Bike[]>({
    queryKey: ['bikes', profile?.role, profile?.branch_id],
    queryFn: () => getAllBikes(profile),
    enabled: !!profile,
  });

  const deleteBikeMutation = useMutation({
    mutationFn: deleteBike,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bikes'] });
      toast({ title: "Bicicleta eliminada correctamente" });
      setIsConfirmDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error al eliminar bicicleta",
        description: error.message
      });
    },
  });

  const handleAddBike = () => {
    setSelectedBike(null);
    setIsFormOpen(true);
  };

  const handleEditBike = (bike: Bike) => {
    setSelectedBike(bike);
    setIsFormOpen(true);
  };

  const handleDeleteBike = (bike: Bike) => {
    setSelectedBike(bike);
    setIsConfirmDeleteDialogOpen(true);
  };

  const handleRepairHistory = (bike: Bike) => {
    setSelectedBike(bike);
    setIsRepairHistoryModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedBike) {
      deleteBikeMutation.mutate(selectedBike.id);
    }
  };

  const renderBikesTable = (bikesToRender: Bike[]) => (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Número</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Modelo</TableHead>
            <TableHead>Color</TableHead>
            <TableHead>Tamaño</TableHead>
            <TableHead className="w-[180px]">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bikesToRender.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-4">
                No hay bicicletas registradas
              </TableCell>
            </TableRow>
          ) : (
            bikesToRender.map((bike) => (
              <TableRow key={bike.id}>
                <TableCell className="py-2">{bike.number}</TableCell>
                <TableCell className="font-medium py-2">{bike.type}</TableCell>
                <TableCell className="py-2">{bike.model}</TableCell>
                <TableCell className="py-2">{bike.color}</TableCell>
                <TableCell className="py-2">{bike.size || '-'}</TableCell>
                <TableCell className="py-2">
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => handleRepairHistory(bike)} title="Historial de Reparaciones">
                      <History size={16} />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => handleEditBike(bike)} title="Editar">
                      <Edit size={16} />
                    </Button>
                    <Button variant="outline" size="icon" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteBike(bike)} title="Eliminar">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  const isAdmin = profile?.role === 'super_admin';

  const groupedBikes = isAdmin && bikes ? bikes.reduce((acc, bike) => {
     const branchName = bike.branch?.name || "Sin Sucursal";
     if (!acc[branchName]) {
       acc[branchName] = [];
     }
     acc[branchName].push(bike);
     return acc;
  }, {} as Record<string, Bike[]>) : {};

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Bicicletas</h1>
          <Button onClick={handleAddBike} className="flex items-center gap-2">
            <Plus size={16} />
            Nueva Bicicleta
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-4">Cargando bicicletas...</div>
        ) : isAdmin ? (
           Object.keys(groupedBikes).length === 0 ? (
             <div className="text-center py-4">No hay bicicletas registradas</div>
           ) : (
             <Accordion type="single" collapsible className="w-full space-y-4">
               {Object.entries(groupedBikes).map(([branchName, branchBikes]) => (
                 <AccordionItem value={branchName} key={branchName} className="bg-white shadow-md rounded-lg">
                   <AccordionTrigger className="text-xl font-bold text-gray-800 px-4 py-3">
                     {branchName} ({branchBikes.length})
                   </AccordionTrigger>
                   <AccordionContent className="px-4 py-2">
                     {renderBikesTable(branchBikes)}
                   </AccordionContent>
                 </AccordionItem>
               ))}
             </Accordion>
           )
        ) : (
           renderBikesTable(bikes || [])
        )}
      </div>

      {/* Bike Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedBike ? "Editar Bicicleta" : "Nueva Bicicleta"}
            </DialogTitle>
            <DialogDescription>
              Actualice la información de la Bicicleta.
            </DialogDescription>
          </DialogHeader>
          <BikeForm
            bike={selectedBike}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['bikes'] });
              toast({ title: selectedBike ? "Bicicleta actualizada correctamente" : "Bicicleta creada correctamente" });
              setIsFormOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={isConfirmDeleteDialogOpen} onOpenChange={setIsConfirmDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>¿Estás seguro que deseas eliminar esta bicicleta?</p>
            <p className="font-medium">{selectedBike?.number} - {selectedBike?.model} - {selectedBike?.type}</p>
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setIsConfirmDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDelete} disabled={deleteBikeMutation.isPending}>
              {deleteBikeMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair History Modal */}
      <RepairHistoryModal
        isOpen={isRepairHistoryModalOpen}
        onClose={() => setIsRepairHistoryModalOpen(false)}
        bike={selectedBike}
      />
    </MainLayout>
  );
};

export default Bikes;